import React, { useEffect, useRef, useState } from "react";

import "../../App.scss";

import CardActions from "@mui/material/CardActions";

import ProjectMetadata, { ExportState, UserReference } from "../../models/ProjectMetadata";

import axios, { showNotificationIfUserOffline } from "../../utils/axios";
import InviteUserDialog from "./InviteUserDialog";

import universalstorageHandler, { enumStorageMode } from "../../persistence/UniversalstorageHandler";
import ZipStorageHandler from "../../persistence/ZipStorageHandler";
import ConfirmDeleteProject from "./ConfirmDeleteProject";
import { loadProject } from "../LoadProject";
import { showToast, showErrToast, updateToastProgress } from "../Toasts";
import ConfirmDialog from "../../modelDialogs/ConfirmDialog";

import { useNavigate } from "react-router-dom";
import { exportProjectAsJSON } from "../../export";
import BaseDialog from "../../modelDialogs/BaseDialog";

import Logger from "../../Logger";
import ProjectHandleling from "./ProjectHandeling";

import { isBetaVersion, isUiModeAdmin, isUiModeExpert } from "../../uiMode";

import InstallationDialog from "./InstallationDialog";
import { formatDateTime } from "../../models/utils";
import SyncManager from "../../SyncManager";
import { itx, noItx } from "../../translationItx/itx";

import compareProjects from "../../CompareProjects";
import { isDevelopmentEnvironment } from "../../estada-config";
import SelectSnapshotDiaog from "../dialogs/SelectSnapshotDiaog";
import { EventKey } from "../../eventLogger/EventKeys";
import { ApplicationMode } from "../../models/SettingsEnums";

const logger = new Logger();

export default function ProjectCard(props: {
  id: string;
  projectMetadata: ProjectMetadata;
  setUploadRunning: (v: boolean) => void;
  fetchProjects: (fromDelete?: boolean) => void;
  setFavorieProject: (id: string) => void;
  setExpanded: (id: string) => void;
  isExpanded: string;
  projectHandleling: ProjectHandleling;
  latestAutobackup?: ProjectMetadata;
}) {
  const {
    id,
    projectMetadata,
    setUploadRunning,
    fetchProjects,
    setFavorieProject,
    setExpanded,
    isExpanded,
    projectHandleling,
    latestAutobackup,
  } = props;

  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [favoritSelected, setFavoritSelected] = useState(state.settings.favoriteSelectedProjects.indexOf(id) != -1);
  const [showAllButtons, setShowAllButtons] = useState(isExpanded == id);
  //const [selectedSnapshotId, setSnapshot] = useState(projectMetadata.id);

  useEffect(() => {
    setShowAllButtons(isExpanded == id);
  });
  const interval = useRef<any>(0);

  const projectLoadErr = itx("homePage.projectLoadError", "project Load Error");

  function loggedInUserIsAdmin(): boolean {
    let res = false;
    const currentUsers = projectMetadata?.users;
    currentUsers.forEach((user) => {
      if (
        user.email == state.loggedInUser.email &&
        (user.status == undefined || user.status == "") &&
        user.id &&
        user.id != ""
      ) {
        res = true;
      }
    });
    return res;
  }

  useEffect(() => {
    const favoriteSelectedProjects = state.settings.getFavoriteSelectedProjects();
    if (favoriteSelectedProjects.indexOf(id) != -1) {
      setFavoritSelected(true);
    } else {
      setFavoritSelected(false);
    }
  });

  useEffect(() => {
    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, []);

  const editBlurPennding = useRef(false);

  function renameProject(newProjectName: string, ev: any) {
    const projectnames = new Array<string>();
    state.allProjectsMap.forEach((project) => {
      if (project.name != projectMetadata.name) {
        projectnames.push(project.name);
      }
    });
    if (
      projectMetadata.name != newProjectName &&
      !state.noDoubleValue.doubleName(newProjectName, projectnames) &&
      newProjectName != ""
    ) {
      projectMetadata.name = newProjectName;
      // Update the server-side modified timestamp so that
      // checkIfProjectIsModifiedOnServerSinceCopiedLocal() returns true and
      // the project is not incorrectly loaded from a stale local copy.
      projectMetadata.lastModifiedOnServer = new Date();
      universalstorageHandler.saveProjectMetaData(projectMetadata);
    } else {
      const text = itx("homePage.projectNameExists", "project Name Exists", {
        T1: newProjectName,
        T2: projectMetadata.name,
      });
      alert(text);
      ev.target.innerText = projectMetadata.name;
    }
  }

  function InviteButton() {
    const label = itx("homePage.inviteExplanation", "Personen zum Projekt einladen");
    return (
      <button
        type="button"
        className="button"
        aria-label={label}
        data-testid={`project-invite-${projectMetadata.id}`}
        onClick={(event) => {
          event.stopPropagation();
          state.dialogManager.openDialog(<InviteUserDialog project={projectMetadata} alreadyInvitedUsers={[]} />);
        }}
      >
        <i className="las la-user-plus" aria-hidden="true" />
      </button>
    );
  }

  function DeleteButton(props: { style: any; buttonsAreCollapsed?: boolean }) {
    const { style, buttonsAreCollapsed } = props;
    function onConfirmDeleteProject() {
      const userInProject = projectMetadata.users.filter((user) => user.email == state.loggedInUser.email);

      setShowAllButtons(false);
      if (projectMetadata.users.length == 1 || loggedInUserIsAdmin()) {
        // delete project
        universalstorageHandler.deleteProject(projectMetadata.id).then((ok) => {
          if (ok) {
            if (loggedInUserIsAdmin()) {
              showToast({
                text: itx("homePage.projectDeleted", "Project Deleted"),
              });
            } else {
              state.allProjectsMap.delete(id);
              showToast({
                text: itx("homePage.projectRemoved", "Project Removed"),
              });
            }
          } else {
            showToast({
              text: itx("homePage.projectDeleted", "Unable to delete Project"),
            });
          }
          state.allProjectsMap.delete(projectMetadata.id);
          fetchProjects(true);
        });
      } else {
        // remove user from project users
        const payload = {
          emailOfFriend: state.loggedInUser.email?.toLowerCase(),
          projectId: projectMetadata.id,
        };
        axios
          .delete("/api/project/invite/user", {
            // no itx
            data: payload,
          })
          .then((result) => {
            if (result.data.STATUS === "SUCCESS") {
              showToast({
                text: itx("homePage.projectDeleted", "Project Deleted"),
              });
              projectMetadata.users = projectMetadata.users.filter(
                (user: { email: string }) => user.email !== state.loggedInUser.email,
              );
            }

            state.allProjectsMap.delete(projectMetadata.id);
            fetchProjects(true);
          })
          .catch(() => {
            state.dialogManager.openDialog(
              <BaseDialog
                title={itx("error", "Error")}
                onOk={() => {
                  return false;
                }}
                okCaption={itx("ok", "Ok")}
                noAbortButton={true}
              >
                <div style={{ padding: "20px" }}>{itx("setPassword.somthingWentwrong", "Something Went wrong.")}</div>
              </BaseDialog>,
            );
          });
      }

      return true;
    }
    function clickDeleteProject() {
      state.dialogManager.openDialog(
        <ConfirmDialog
          className="dialog-standard"
          title={itx("homePage.projectDelete", `Project Delete`)}
          text={itx("homePage.projectDeleteText", `Do you want to delete this project: ${projectMetadata.name}`, {
            T1: projectMetadata.name,
          })}
          onOk={onConfirmDeleteProject}
          onClose={() => {
            return true;
          }}
          hideXButton={true}
        />,
      );
    }
    function clickProjectLicences() {
      state.dialogManager.openDialog(
        <ConfirmDialog
          className="dialog-standard"
          title={itx("homePage.projectDeleteDisabeled", `Projekt kann nicht gelöscht werden`)}
          text={itx(
            "homePage.projectDeleteDisabeledText",
            `Das Projekt T1 kann nicht gelöscht werden, da mindestens eine Lizenz vorhanden ist.`,
            {
              T1: projectMetadata.name,
            },
          )}
          onOk={() => {
            return true;
          }}
          noAbortButton={true}
          hideXButton={true}
          okCaption={itx("ok", "Ok")}
        />,
      );
    }
    function clickUserFromProjec() {
      state.dialogManager.openDialog(
        <ConfirmDialog
          className="dialog-standard"
          title={itx("homePage.projectRemove", `Project Remove`)}
          text={itx("homePage.projectRemoveText", "Möchten Sie dieses Projekt: T1 aus Ihrer Liste entfernen?", {
            T1: projectMetadata.name,
          })}
          onOk={onConfirmDeleteProject}
          onClose={() => {
            return true;
          }}
          hideXButton={true}
        />,
      );
    }

    function DeleteButtonInner() {
      return (
        <button
          style={style}
          className="button"
          onClick={(ev) => {
            ev.stopPropagation();
            if (projectMetadata.projectLicence?.invoiceIds?.length > 0 && !isDevelopmentEnvironment()) {
              clickProjectLicences();
            } else if (loggedInUserIsAdmin()) {
              clickDeleteProject();
            } else {
              clickUserFromProjec();
            }
          }}
        >
          <i className={`las la-trash`}></i>
        </button>
      );
    }
    return <DeleteButtonInner />;
  }

  function OpenSnapShotSelectionDialog(props: { style: any }) {
    const { style } = props;

    return (
      <button
        style={style}
        className="button"
        onClick={(ev) => {
          ev.stopPropagation();
          state.dialogManager.openDialog(<SelectSnapshotDiaog projectMetadata={projectMetadata} navigate={navigate} />);
        }}
      >
        <i className={`las la-edit`}> {itx("")}</i>
      </button>
    );
  }

  function DownloadLocalBackupButton(props: { style: any; buttonsAreCollapsed?: boolean }) {
    const { style, buttonsAreCollapsed } = props;
    async function clickDownloadProject() {
      logger.log("clicked download");
      try {
        const users = projectMetadata.users;
        projectMetadata.users = new Array<UserReference>();
        await universalstorageHandler.loadProject(projectMetadata.id);

        const z = new ZipStorageHandler();
        const ok = await z.saveProjectItems(projectMetadata.id);
        const ok1 = await z.saveProjectMetadata(projectMetadata.id, state.projectMetadata.toJSON());

        const blob = await z.getBlob();
        const elem = window.document.createElement("a");
        elem.href = window.URL.createObjectURL(blob);
        elem.download = projectMetadata.name + ".estada";
        document.body.appendChild(elem);
        elem.click();
        document.body.removeChild(elem);
        projectMetadata.users = users;
        showToast({
          text: itx("homePage.projectDownloaded", `T1 wurde auf Ihren Rechner herunter geladen`, {
            T1: projectMetadata.name,
          }),
        });
      } catch (ex) {
        logger.log("", ex);
      }
    }

    return (
      <button
        style={style}
        className="button"
        onClick={(ev) => {
          ev.stopPropagation();
          clickDownloadProject();
        }}
      >
        <i className={`las la-save`}></i>
        {/* {itx("homePage.download", "Download")} */}
      </button>
    );
  }

  function DownloadETSExcangeButton(props: { style: any; buttonsAreCollapsed?: boolean }) {
    const { style } = props;
    async function clickDownloadProject() {
      logger.log("clicked DownloadETSExcangeButton");
      await exportProjectAsJSON(undefined);
      showToast({
        text: itx("homePage.exchangeDataCreated", "Austauschdatei wurde erstellt"),
      });
    }

    return (
      <button
        style={style}
        className="button"
        onClick={(ev) => {
          ev.stopPropagation();
          clickDownloadProject();
        }}
      >
        <i className={`las la-save`}></i>
        {/* {itx("homePage.download", "Download")} */}
      </button>
    );
  }

  function OpenButton(props: { style: any; buttonsAreCollapsed: boolean; openInConceptOne?: boolean; openInEstada?: boolean }) {
    const { style, buttonsAreCollapsed, openInConceptOne = false, openInEstada = false } = props;
    const [isOpening, setIsOpening] = useState(false);
    const opensConceptOne = !openInEstada && (openInConceptOne || projectMetadata.applicationMode === ApplicationMode.ConceptOne);
    const targetPath = opensConceptOne ? "/concept-one" : "/app";
    const appIcon = opensConceptOne
      ? `${state.hostBaseUrl}images/logo-c1-small-2.png`
      : `${state.hostBaseUrl}images/logo-detail-small-2.png`;
    const editLabel = openInConceptOne
      ? itx("homePage.openInConceptOne", "Projekt in Concept One öffnen")
      : openInEstada ? itx("homePage.openInEstada", "Projekt in der Detail-Planung öffnen")
      : itx("homePage.edit", "Bearbeiten");

    async function clickOpenProject() {
      const selectedMethadata = projectMetadata;

      // if (state.loggedInUser.abo == "no" && selectedMethadata.statistic) {
      //   if (selectedMethadata.statistic.numberOfActors + selectedMethadata.statistic.numberOfButtons > 10) {
      //     state.dialogManager.openDialog(
      //       <BaseDialog
      //         noAbortButton={true}
      //         title={itx("projectCard.openNotAllowed", "Estada Demo-Version")}
      //         okCaption={itx("projectCard.toAbos", "Estada abonieren")}
      //         onOk={() => {
      //           window?.open("https://estada.de/pricing_app", "_blank")?.focus();
      //           return true;
      //         }}
      //       >
      //         <div style={{ textAlign: "center" }}>
      //           {itx("projectCard.aboLimitReached", "Dieses Projekt ist komplex und")} <br />
      //           {itx("projectCard.aboLimitReached", "kann nur mit einem Abo bearbeitet werden.")}
      //         </div>
      //       </BaseDialog>,
      //     );

      //     return null;
      //   }
      // }

      let blob: Blob | undefined = undefined;
      if (selectedMethadata?.className != "ProjectMetadata") {
        console.error("Snapchat no methadata " + selectedMethadata.id);
        return;
      }

      if (
        selectedMethadata.checkIfProjectIsModifiedOnServerSinceCopiedLocal() &&
        selectedMethadata.checkIfProjectIsModifiedLocal()
      ) {
        showToast({
          text: itx("projectCard.changesFromServerAccepted", "Änderungen am Projekt werden vom Server übernommen"),
        });
        const syncManager = new SyncManager(selectedMethadata);
        blob = await loadProject(selectedMethadata.id, true);
        syncManager.mergeIncomingVersionFromServer();
      } else {
        //
        // Hier kommen wir online und offline an,
        //    wenn das Project nicht gemerged werden muß

        let loadLocal = selectedMethadata.checkIfProjectIsModifiedLocal();
        // mwe 11.4.2026 loadLocal = false;

        if (
          selectedMethadata.checkIfProjectIsLocalStored() &&
          !selectedMethadata.checkIfProjectIsModifiedOnServerSinceCopiedLocal()
        ) {
          //
          // zur Peformance Optimierung können wir hier
          // das Projekt lokal laden wenn es offline verfügbar und aktuell ist ....
          //
          // Rein aus Sicherheitsgründen habe ich das zunächst noch vertagt....
          // MWE 4.10.2024 && 26.1.205
          //
          ///////////////////// enable this line:
          loadLocal = true; // mwe 11.4.2016
        }

        if (!isBetaVersion()) {
          loadLocal = false;
        }

        blob = await loadProject(selectedMethadata.id, loadLocal);

        if (blob && selectedMethadata.checkIfProjectIsNeedToBeCopiedLocal()) {
          universalstorageHandler.saveLocalCopyOfProject2(blob, false);
        }
      }

      // START PROJECT COMPARISON IF window.compareState exists.
      if (window.compareState) {
        compareProjects(window.state, window.compareState);
      }

      //
      // hide loading toast
      //
      updateToastProgress({ progress: 100 });

      logger.log(`loadProject ${selectedMethadata.id} ${blob}`);
      if ((navigator.onLine || universalstorageHandler.storageMode == enumStorageMode.local) && blob) {
        navigate(targetPath);
        // state.navigate("/app");// cant be usd becaus navigate in state is undefined
      } else {
        if (!navigator.onLine && universalstorageHandler.storageMode == enumStorageMode.server) {
          showNotificationIfUserOffline();
        }
        if (navigator.onLine && !blob) {
          showErrToast(projectLoadErr);
        }
      }
    }

    return (
      <button
        style={style}
        className="button"
        disabled={isOpening}
        title={editLabel}
        aria-label={editLabel}
        onClick={(ev) => {
          ev.stopPropagation();
          setIsOpening(true);
          void clickOpenProject()
            .catch((error) => {
              console.error("Project could not be opened", projectMetadata.id, error);
              showErrToast(projectLoadErr);
            })
            .finally(() => setIsOpening(false));
        }}
      >
        <img
          src={appIcon}
          alt=""
          aria-hidden="true"
          style={{
            display: "block",
            width: opensConceptOne ? "60px" : "55px",
            height: opensConceptOne ? "60px" : "55px",
            objectFit: "contain",
            marginBottom: "25px",
            marginRight: "15px",
            transform: opensConceptOne
              ? "translateY(-3px)" // no itx
              : undefined,
            filter: "drop-shadow(0 2px 3px rgba(0, 0, 0, 0.65))",
          }}
        />
        {buttonsAreCollapsed && (
          <span style={{ marginRight: "10px" }}>
            {isOpening ? `${itx("homePage.loading", "Loading...")}...` : editLabel}
          </span>
        )}
      </button>
    );
  }

  function DuplicateButton(props: { style: any; buttonsAreCollapsed: boolean }) {
    const { style, buttonsAreCollapsed } = props;

    function duplicateProject(ev: any) {
      projectHandleling.copyProject(projectMetadata.id, ev, navigate);
      state.eventLogger.addEvent(EventKey.ProjectCreated, {
        projectId: projectMetadata.id,
        creationType: "Duplicated",
      });
    }
    return (
      <button
        style={style}
        className="button"
        onClick={(ev) => {
          ev.stopPropagation();
          duplicateProject(ev);
        }}
      >
        {!buttonsAreCollapsed && <i className={`las la-clone`}></i>}
        {buttonsAreCollapsed && itx("homePage.duplicate", "Duplicate")}
      </button>
    );
  }

  async function loadCompareProject() {
    showToast({
      text: itx("projectCad.compatissonProjectLoading", "Vergleichsprojekt wird geladen... \n Bitte warten"),
      keepVisible: true,
      modal: true,
    });

    await universalstorageHandler.loadCompareProject(projectMetadata.id);

    showToast({ text: itx("projectCad.compatissonProjectLoaded", "Vergleichsprojekt wurde geladen") });
  }

  function SelectForCompareButton(props: { style: any; buttonsAreCollapsed: boolean }) {
    const { style, buttonsAreCollapsed } = props;

    return (
      <button style={style} className="button" onClick={loadCompareProject}>
        {!buttonsAreCollapsed && <i className={`las la-exchange-alt`}></i>}
        {buttonsAreCollapsed && itx("homePage.selectForCompare", "selectForCompare")}
      </button>
    );
  }

  function InstallationButton(props: { style: any; projectMetadata: any; buttonsAreCollapsed?: boolean }) {
    const { style } = props;
    function clickInstallationButton() {
      logger.log("clicked InstallationButton");
      state.dialogManager.openDialog(
        <InstallationDialog
          style={style}
          projectMetadata={projectMetadata}
          key={`${projectMetadata.id}_InstallationButton`}
        />,
      );
    }

    return (
      <button
        style={style}
        className="button"
        onClick={(ev) => {
          ev.stopPropagation();
          clickInstallationButton();
        }}
      >
        <i className={`las la-cog`}></i>
      </button>
    );
  }

  function MoreButtons(props: { style: any; buttonsAreCollapsed?: boolean }) {
    const { style, buttonsAreCollapsed } = props;

    return (
      <button
        style={style}
        className="button"
        onClick={(ev) => {
          setExpanded(id);
          setShowAllButtons(true);
        }}
      >
        {/* <i className={`las la-caret-square-up`}></i> */}
        <i className="las la-ellipsis-h"></i>
      </button>
    );
  }

  function CollapseButton(props: {}) {
    return (
      <button
        className="button circle"
        style={{ alignSelf: "center" }}
        onClick={(ev) => {
          setExpanded(id);
          setShowAllButtons(false);
        }}
      >
        {/* <i className={`las la-caret-square-up`}></i> */}
        <i className="las la-angle-up"></i>
      </button>
    );
  }

  function Content() {
    const disableEdit = universalstorageHandler.storageMode == enumStorageMode.local;

    function setCaret(elm: HTMLElement, pos: number) {
      const range = document.createRange();
      const sel = window.getSelection();

      range.setStart(elm.childNodes[0], pos);
      range.collapse(true);

      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    return (
      <div
        style={{
          position: "relative",
          padding: "10px",
          paddingBottom: "0px",
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          gap: "5px",
        }}
      >
        <OnlineStatus projectMetadata={projectMetadata} />

        <div
          id="main"
          style={{
            position: "relative",
            width: "300px",
            display: "flex",
            flexDirection: "column",
            flexGrow: "1",
            flexShrink: "1",
            paddingLeft: "12px",
          }}
        >
          <div
            id={`${projectMetadata.id}_projectName`}
            data-noitx={noItx("true")}
            style={{
              fontSize: "1.5em",
              backgroundColor: "var(--primary-color)",
              pointerEvents: disableEdit ? "none" : "auto",
              cursor: disableEdit ? "default" : "text",
            }}
            onMouseDown={(ev) => ev.stopPropagation()}
            onMouseUp={(ev) => ev.stopPropagation()}
            onClick={(ev) => ev.stopPropagation()}
            contentEditable={!disableEdit}
            suppressContentEditableWarning={true}
            onFocus={() => (editBlurPennding.current = true)}
            onBlur={(ev) => {
              const newProjectName = ev.target.innerText.trim();
              if (newProjectName != projectMetadata.name) {
                renameProject(newProjectName, ev);
                setTimeout(() => {
                  editBlurPennding.current = false;
                }, 100);
              }
            }}
            // onChange={(ev) => renameProject(ev)}
            onKeyDown={(ev: any) => {
              if (ev.keyCode == 13) {
                ev.target.blur();
              }
            }}
          >
            {projectMetadata.name}
          </div>

          <div
            id={`${projectMetadata.id}_description`}
            data-noitx={noItx("true")}
            style={{
              backgroundColor: "var(--primary-color)",
              pointerEvents: disableEdit ? "none" : "auto",
              cursor: disableEdit ? "default" : "text",
              fontSize: "16px",
            }}
            onMouseDown={(ev) => ev.stopPropagation()}
            onMouseUp={(ev) => ev.stopPropagation()}
            onClick={(ev) => ev.stopPropagation()}
            contentEditable={!disableEdit}
            suppressContentEditableWarning={true}
            onFocus={() => (editBlurPennding.current = true)}
            onBlur={(ev) => {
              const newProjectDescription = ev.target.innerText.trim();
              if (newProjectDescription != projectMetadata.description) {
                projectMetadata.description = newProjectDescription;
                universalstorageHandler.saveProjectMetaData(projectMetadata);
                setTimeout(() => {
                  editBlurPennding.current = false;
                }, 100);
              }
            }}
            onKeyDown={(ev: any) => {
              if (ev.keyCode == 13) {
                ev.target.blur();
              }
            }}
          >
            {projectMetadata.description}
          </div>

          {latestAutobackup && (
            <div
              style={{
                color: "#b35a00",
                fontSize: "13px",
                fontWeight: 600,
                marginTop: "4px",
                pointerEvents: "none",
              }}
            >
              <i className="las la-history" style={{ marginRight: "5px" }} />
              {itx("projectCard.autobackupAvailable", "Autobackup vorhanden")}: {" "}
              {formatDateTime(latestAutobackup.localCopied)}
            </div>
          )}

          {projectMetadata.users?.length > 0 && isUiModeAdmin() && (
            <div
              style={{
                backgroundColor: "var(--primary-color)",
                pointerEvents: "none",
                cursor: "default",
                fontSize: "16px",
              }}
            >
              {itx("projectCard.createdBy", "Created By")}:{" "}
              {projectMetadata.users[0].name || projectMetadata.users[0].email}
            </div>
          )}

          <OfflineInfoText projectMetadata={projectMetadata} />

          <div
            style={{
              backgroundColor: "var(--primary-color)",
              pointerEvents: "none",
              cursor: "default",
              fontSize: "12px",
            }}
          >
            {itx("fileUpload.createdAt", "Erstellt")}: {projectMetadata.created.getDate()}.
            {projectMetadata.created.getMonth() + 1}.{projectMetadata.created.getFullYear()}
          </div>

          <div
            style={{
              backgroundColor: "var(--primary-color)",
              pointerEvents: "none",
              cursor: "default",
              fontSize: "12px",
            }}
          >
            {itx("fileUpload.lastModifiedDate")}: {projectMetadata.lastModifiedOnServer.getDate()}.
            {projectMetadata.lastModifiedOnServer.getMonth() + 1}.{projectMetadata.lastModifiedOnServer.getFullYear()}
          </div>
        </div>

        <LizenceInfo />

        <FavoriteSelect />
      </div>
    );
  }

  function LizenceInfo(props: {}) {
    const lizenses = projectMetadata.projectLicence;
    if (!lizenses || lizenses.invoiceIds.length == 0) {
      return <div style={{ width: "20px", flexGrow: "0", flexShrink: "0" }}></div>;
    }
    return (
      <i
        className={"las la-id-badge"}
        style={{
          position: "relative",
          width: "20px",
          textAlign: "center",
          color: "#373737b3",
          cursor: "pointer",
          flexGrow: "0",
          flexShrink: "0",
        }}
        data-tooltip-id="tv-tooltip"
        data-tooltip-content={itx("projectCard.licencesInfo", "Lizenz vorhanden")}
        data-tooltip-place="right"
        onClick={(ev) => {
          // check here if project is renamed
        }}
      ></i>
    );
  }

  function FavoriteSelect() {
    let icoName = "lar la-star"; // star empty  // no itx
    if (favoritSelected) {
      icoName = "las la-star"; // star full  // no itx
    }
    return (
      <i
        className={icoName}
        style={{
          position: "relative",
          width: "20px",
          textAlign: "center",
          color: "#373737b3",
          cursor: "pointer",
          flexGrow: "0",
          flexShrink: "0",
        }}
        onClick={(ev) => {
          // check here if project is renamed
          const projectNameElement = document.getElementById(`${projectMetadata.id}_projectName`);
          const newProjectName = projectNameElement?.textContent;
          if (newProjectName && newProjectName != projectMetadata.name) {
            renameProject(newProjectName, ev);
            setTimeout(() => {
              editBlurPennding.current = false;
            }, 100);
          }
          setFavoritSelected(!favoritSelected);
          setFavorieProject(id);
          setShowAllButtons(false);
          if (!favoritSelected) {
            setTimeout(() => {
              const elm = document.getElementById(id);
              elm?.scrollIntoView({
                behavior: "smooth",
                block: "start", // no itx
                inline: "nearest",
              });
            }, 200);
          }
        }}
      ></i>
    );
  }

  function Buttons() {
    const styleExplanation: any = { verticalAlign: "middle" };
    const buttonsUncolapsed = new Map<string, any>();
    const buttonsUncollapsedView = new Array<any>();
    buttonsUncolapsed.set(
      itx("homePage.editExplanation", "editExplanation"),
      <OpenButton style={{}} buttonsAreCollapsed={false} key={`${projectMetadata.id}_OpenButton`} />,
    );
    if (projectMetadata.isC1Project && projectMetadata.applicationMode === ApplicationMode.Main) {
      buttonsUncolapsed.set(
        itx("homePage.openInConceptOne", "Projekt in Concept One öffnen"),
        <OpenButton style={{}} buttonsAreCollapsed={false} openInConceptOne key={`${projectMetadata.id}_OpenConceptOneButton`} />,
      );
    }
    if (projectMetadata.isEstadaProject && projectMetadata.applicationMode === ApplicationMode.ConceptOne) {
      buttonsUncolapsed.set(
        itx("homePage.openInEstada", "Projekt in der Detail-Planung öffnen"),
        <OpenButton style={{}} buttonsAreCollapsed={false} openInEstada key={`${projectMetadata.id}_OpenEstadaButton`} />,
      );
    }
    // buttonsUncolapsed.set(
    //   itx(`homePage.${etsText}`, etsText),
    //   <ExportButton style={style} buttonsAreCollapsed={false} key={`${projectMetadata.id}_ExportButton`} />,
    // );

    buttonsUncolapsed.set(
      itx("homePage.loadingESTADAExplanation"),
      <DownloadLocalBackupButton style={{}} key={`${projectMetadata.id}_DownloadLocalBackupButton`} />,
    );
    if (
      /*aboTypeInfo(aboTypeFromString(state.loggedInUser.abo)).dublicateAllowed*/ true ||
      isDevelopmentEnvironment()
    ) {
      buttonsUncolapsed.set(
        itx("homePage.duplicationProjectExplanation", "Projekt duplizieren"),
        <DuplicateButton style={{}} buttonsAreCollapsed={false} key={`${projectMetadata.id}_DuplicateButton`} />,
      );
    }
    buttonsUncolapsed.set(
      itx("homePage.selectForCompare", "Als Vergleichsprojekt wählen"),
      <SelectForCompareButton style={{}} buttonsAreCollapsed={false} key={`${projectMetadata.id}_DuplicateButton`} />,
    );
    // buttonsUncolapsed.set(
    //   itx("homePage.loadingETSExplanation", "loadingETSExplanation"),
    //   <DownloadETSExcangeButton style={style} key={`${projectMetadata.id}_DownloadETSExcangeButton`} />,
    // );

    buttonsUncolapsed.set(
      itx("homePage.inviteExplanation", "Personen zum Projekt einladen"),
      <InviteButton key={`${projectMetadata.id}_InviteButton`} />,
    );
    buttonsUncolapsed.set(
      itx("homePage.deleteExplanation", "deleteExplanation"),
      <DeleteButton style={{}} key={`${projectMetadata.id}_DeleteButton`} />,
    );
    // buttonsUncolapsed.set(
    //   itx("homePage.installationAssistent", "installationAssistent"),
    //   <InstallationButton
    //     style={{}}
    //     projectMetadata={projectMetadata}
    //     key={`${projectMetadata.id}_InstallationButton`}
    //   />,
    // );

    buttonsUncolapsed.set(itx("homePage.openSnapshotOptions"), <OpenSnapShotSelectionDialog style={{}} />);

    let i = 0;
    buttonsUncolapsed.forEach((button, explanation) => {
      const spacing = button.props.openInConceptOne || button.props.openInEstada ? { paddingTop: "32px" } : undefined;
      buttonsUncollapsedView.push(
        <tr key={`${projectMetadata.id}_tr_${i}`}>
          <td style={spacing}>{button}</td>
          <td style={{ ...styleExplanation, ...spacing }}>{explanation}</td>
        </tr>,
      );
      i++;
    });
    ///////////////////////////////////
    // abo

    function editable(): boolean {
      // check here if project is oldest Project
      if (state.loggedInUser.abo == "personal") {
        let oldesProject = { id: "", date: new Date() };
        state.allProjectsMap.forEach((proj, key) => {
          if (proj.created < oldesProject.date) {
            oldesProject = { id: key, date: proj.created };
          }
        });
        if (oldesProject.id != id) {
          return false;
        }
      }

      return true;
    }
    ///////////
    return (
      <CardActions style={{ alignSelf: "center" }}>
        {!editable() && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {itx(
              "homePage.upgradeSubscriptionToEdit",
              "Sie müssen Ihr Abo upgraden um dieses Projekt bearbeiten zu können",
            )}
            <button
              className="button"
              onClick={() => {
                navigate("/selfservice");
                // window?.open("https://estada.chargebeeportal.com", "_blank")?.focus(); // no itx
              }}
            >
              {itx("homePage.editSubscription", "Abo verwalten")}
            </button>
          </div>
        )}
        {editable() && (
          <div
            className="projectCardButtons"
            style={{
              display: "flex",
              flexDirection: showAllButtons ? "column" : "row",
            }}
          >
            {!showAllButtons && (
              <div
                className="buttonsCollapsedView"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  marginTop: "10px",
                }}
              >
                <OpenButton style={{}} buttonsAreCollapsed={true} />
                <MoreButtons style={{}} />
              </div>
            )}

            {showAllButtons && (
              <table className="buttonsUncollapsedView" style={{ paddingTop: "10px" }}>
                <tbody>{buttonsUncollapsedView}</tbody>
              </table>
            )}
            {showAllButtons && <CollapseButton />}
          </div>
        )}
      </CardActions>
    );
  }

  function Statistic() {
    if (projectMetadata.statistic && isUiModeAdmin()) {
      const styleTR: any = { textAlign: "center", padding: " 0px 10px " };
      const imgStyle: any = { width: "auto", height: `20px`, objectFit: "contain" };
      const collumsDef: Array<{ name: string; icon: string; value: number }> = [
        {
          name: itx("components.plural.Actor", "Aktors"),
          icon: "las la-rotate-90 la-server", // no itx
          value: projectMetadata.statistic.numberOfActors,
        },
        {
          name: itx("components.plural.Button", "Buttons"),
          icon: state.hostBaseUrl + "images/buttons/touch-6.png", // no itx
          value: projectMetadata.statistic.numberOfButtons,
        },
        {
          name: itx("components.plural.Room", "Rooms"),
          icon: "lab la-codepen",
          value: projectMetadata.statistic.numberOfRooms,
        },
        {
          name: itx("components.plural.GroupAdress", "GroupAdress"),
          icon: "las la-grip-vertical groupAddress3", // no itx
          value: projectMetadata.statistic.numberOfGroupAdresses,
        },
        {
          name: itx("components.plural.Connection", "Connections"),
          icon: "las la-link",
          value: projectMetadata.statistic.numberOfConnections,
        },
        {
          name: itx("components.plural.Scene", "Scene"),
          icon: state.hostBaseUrl + "images/various/scene.png", // no itx
          value: projectMetadata.statistic.numberOfScenes,
        },
        {
          name: itx("hours", "hours"),
          icon: "las la-hourglass", // no itx
          value: Math.round(projectMetadata.statistic.hoursInEdit * 100) / 100,
        },
      ];

      const rowIconsTd = new Array<any>();
      const rowValuesTd = new Array<any>();
      let i = 0;
      collumsDef.forEach((collumn) => {
        let elm: any = <img className="icon" style={imgStyle} src={collumn.icon} />;

        if (collumn.icon.startsWith("las la") || collumn.icon.startsWith("lab la")) {
          elm = <i style={imgStyle} className={collumn.icon}></i>;
        }

        rowIconsTd.push(
          <td
            key={collumn.name + `${rowIconsTd.length} ${i} `}
            data-tooltip-id="tv-tooltip"
            data-tooltip-content={collumn.name}
            data-tooltip-place="bottom"
            style={styleTR}
          >
            {elm}
          </td>,
        );
        rowValuesTd.push(
          <td key={`${rowIconsTd.length}  ` + collumn.value + `${rowValuesTd.length} ${i} `} style={styleTR}>
            {collumn.value}
          </td>,
        );
        i++;
      });

      return (
        <div
          style={{ width: "350px", overflowX: "auto", display: "flex", justifyContent: "center", alignSelf: "center" }}
          key={"tableStatistic" + projectMetadata.id} // no itx
        >
          <table>
            <tbody>
              <tr style={{ fontSize: "20px" }}>{rowIconsTd}</tr>
              <tr>{rowValuesTd}</tr>
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  }

  const usersEmail = new Array<any>();
  projectMetadata.users?.forEach((user) => {
    usersEmail.push(<div>{user.email}</div>);
  });
  ///////////////////////////////////////////////////////////////////////////////

  return (
    <div
      id={id}
      key={projectMetadata.id}
      className={"projectSelectionCard"}
      style={{
        maxHeight: showAllButtons ? "700px" : "400px", // Added for transition  // no itx
      }}
    >
      <Content />
      <Buttons />
      {state.loggedInUser.email == "admin@estada.de" && usersEmail}
      {/* {exportState == ExportState.ReadyForDownload && (
        <div>{itx("homePage.etsDownloadReady", "ETS-Project is ready for download")}</div>
      )} */}
      {/* {exportState != ExportState.ReadyForDownload && exportState != ExportState.None && (
        <div>{itx("homePage.etsConversionInProgress", "ETS Conversion is working ...")} </div>
      )} */}
      <ConfirmDeleteProject
        projectToDelete={projectMetadata}
        setOpen={setConfirmOpen}
        open={confirmOpen}
        fetchProjects={fetchProjects}
      />
      <Statistic
        key={"statistic" + projectMetadata.id} // no itx
      />
      {isDevelopmentEnvironment() && (
        <div style={{ fontSize: "10px", color: "grey" }}>{"id: " + projectMetadata.id}</div>
      )}
      {/* number of snapshots:{projectMetadata.snapshots.length} */}
    </div>
  );
}

function OfflineInfoText(props: { projectMetadata: ProjectMetadata }) {
  const { projectMetadata } = props;

  if (universalstorageHandler.storageMode == enumStorageMode.local) {
    const isModifiedLocal = projectMetadata.checkIfProjectIsModifiedLocal();

    return (
      <div style={{ display: "flex", flexDirection: "column", backgroundColor: "#fff2", padding: "10px" }}>
        <div>{itx("homePage.offlineCopy", "Offline Kopie von:")}</div>
        {projectMetadata.snapshots.length == 0 && (
          <div style={{ paddingBottom: "10px" }}>{formatDateTime(projectMetadata.localCopied)}</div>
        )}

        {!isModifiedLocal && <div>{itx("homePage.offlineNoChange", "Offline keine Änderungen")}</div>}
        {isModifiedLocal && <div>{itx("homePage.uploadToServerIsPending", "Upload auf den Server ausstehend")}</div>}
      </div>
    );
  }

  return null;
}

function OnlineStatus(props: { projectMetadata: ProjectMetadata }) {
  const { projectMetadata } = props;

  const projectIsAvailibleLocal = projectMetadata.localCopied != undefined;

  const [projectIsModifiedLocal, setProjectIsModifiedLocal] = useState(false);
  const [needsToBeCopiedLocal, setNeedsToBeCopiedLocal] = useState(false);
  const [needsToBeMergedLocal, setNeedsToBeMergedLocal] = useState(false);

  async function init() {
    const modOnServer = projectMetadata.checkIfProjectIsModifiedOnServerSinceCopiedLocal();
    const modLocal = projectMetadata.checkIfProjectIsModifiedLocal();

    setProjectIsModifiedLocal(modLocal);

    setNeedsToBeCopiedLocal(!modOnServer && !modLocal);
    setNeedsToBeMergedLocal(modOnServer && modLocal);
  }

  useEffect(() => {
    init();
  }, []);

  function OfflineIcon(props: {
    needsToBeCopiedLocal: boolean;
    needsToBeMergedLocal: boolean;
    modifiedOffline: boolean;

    modifiedDate: string;
  }) {
    const { modifiedOffline, modifiedDate, needsToBeCopiedLocal, needsToBeMergedLocal } = props;

    let tooltipText = `Dieses Projekt ist offline verfügbar`;

    let color = "green";

    const icons = new Array<any>();

    if (projectMetadata.checkIfProjectIsModifiedOnServerSinceCopiedLocal()) {
      tooltipText +=
        "\n" +
        itx(
          "homePage.changesNeedToBeSynced",
          "Aber in der Clound gibt es Änderungen die noch synchronisiert werden müssen",
        );
      tooltipText +=
        "\n" +
        itx("homePage.changesWillBeSyncedOnOpen", "Beim Öffnen des Projekts erfolgt die Synchronisierung automatisch");
      color = "red";

      icons.push(
        <i
          key="1"
          style={{ position: "absolute", left: "4px", top: "4px", color: color }}
          className="las la-download"
        />,
      );
    } else if (projectMetadata.checkIfProjectIsNeedToBeCopiedLocal()) {
      tooltipText = itx("homePage.projectIsAvailibleOffline", "Diese Projekt ist noch NICHT offline verfügbar");
      tooltipText +=
        " " + itx("homePage.projectWillBeCopiedOfflineOnOpen", "Durch Öffnen würde es offline kopiert und verfügbar");
      color = "grey";
      icons.push(
        <i key="1" style={{ position: "absolute", left: "4px", top: "4px", color: color }} className="las la-upload" />,
      );
    } else if (projectMetadata.checkIfProjectIsModifiedLocal()) {
      tooltipText += "\n\n" + itx("homePage.projectIsModifiedOffline", "Das Projekt wurde offline bearbeitet");
      if (modifiedDate) {
        tooltipText += "\n" + itx("homePage.lastChangedAt", "zuletzt am: ") + modifiedDate + ".\n";
      }
      tooltipText +=
        "\n" + itx("homePage.projectNeedsToBeStoredOnServer", "Das Projekt muß noch in der Cloud gespeichert werden");
      color = "yellow";
      icons.push(
        <i key="1" style={{ position: "absolute", left: "4px", top: "4px", color: color }} className="las la-upload" />,
      );
    } else if (projectMetadata.checkIfProjectIsLocalStored() && !projectMetadata.checkIfProjectIsModifiedLocal()) {
      if (universalstorageHandler.storageMode == enumStorageMode.local) {
        tooltipText += "\n" + itx("homePage.andIsNotModified", "und ist noch unverändert");
      }

      icons.push(
        <i
          key="2"
          style={{ position: "absolute", left: "2px", top: "2px", color: color, fontSize: "1.2em" }}
          className="las la-rotate-90 la-slash"
        />,
      );
      icons.push(
        <i key="3" style={{ position: "absolute", left: "2px", top: "2px", color: color }} className="las la-signal" />,
      );
    }

    const tooltipHtml = `<div>${tooltipText.replaceAll("\n", "<br/>")}</div>`;

    return (
      <div
        style={{
          position: "relative",
          backgroundColor: "#fff4",
          width: "26px",
          height: "26px",
          borderRadius: "30px",
          border: `1px solid ${color}`,
          flexGrow: "0",
          flexShrink: "0",
        }}
        data-tooltip-id="tv-tooltip"
        data-tooltip-html={tooltipHtml}
        data-tooltip-place="right"
      >
        {icons}
      </div>
    );
  }

  if (universalstorageHandler.storageMode == enumStorageMode.server) {
    //
    //  in Online Mode
    //

    return (
      <OfflineIcon
        needsToBeCopiedLocal={needsToBeCopiedLocal}
        needsToBeMergedLocal={needsToBeMergedLocal}
        modifiedOffline={false}
        modifiedDate={formatDateTime(projectMetadata.lastModifiedOnServer)}
      />
    );
  } else {
    //
    //  in Offline Mode
    //

    return (
      <OfflineIcon
        needsToBeCopiedLocal={false}
        needsToBeMergedLocal={false}
        modifiedOffline={projectIsModifiedLocal != undefined}
        modifiedDate={formatDateTime(projectMetadata.lastModifiedOnServer)}
      />
    );

    return (
      <OfflineIcon
        needsToBeCopiedLocal={false}
        needsToBeMergedLocal={false}
        modifiedOffline={projectIsModifiedLocal != undefined}
        modifiedDate={formatDateTime(projectMetadata.lastModifiedOnServer)}
      />
    );
  }
  return null;
}
